import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ArrowDown,
  ArrowUp,
  Clock,
  Lightbulb,
  TrendUp,
  Users,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

interface SmartSuggestionsProps {
  selectedDatasetCount: number;
  hasQualityReports: boolean;
  hasCorrelations: boolean;
  currentTab: string;
  onSuggestionClick: (action: string) => void;
}

interface Suggestion {
  id: string;
  title: string;
  description: string;
  action: string;
  icon: any;
  priority: 'high' | 'medium' | 'low';
  category: 'next-step' | 'optimization' | 'learning';
}

export function SmartSuggestions({
  selectedDatasetCount,
  hasQualityReports,
  hasCorrelations,
  currentTab,
  onSuggestionClick,
}: SmartSuggestionsProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const generateSuggestions = (): Suggestion[] => {
    const suggestions: Suggestion[] = [];

    // Next step suggestions
    if (selectedDatasetCount === 0) {
      suggestions.push({
        id: 'start-template',
        title: 'Start with a Research Template',
        description: 'Choose a pre-configured template for faster setup',
        action: 'templates',
        icon: Lightbulb,
        priority: 'high',
        category: 'next-step',
      });
    } else if (selectedDatasetCount === 1) {
      suggestions.push({
        id: 'add-more-datasets',
        title: 'Add More Datasets',
        description:
          'Select additional datasets to enable correlation analysis',
        action: 'discover',
        icon: TrendUp,
        priority: 'high',
        category: 'next-step',
      });
    }

    if (selectedDatasetCount > 0 && !hasQualityReports) {
      suggestions.push({
        id: 'check-quality',
        title: 'Check Data Quality',
        description: 'Validate your data before analysis',
        action: 'quality',
        icon: TrendUp,
        priority: 'high',
        category: 'next-step',
      });
    }

    if (selectedDatasetCount >= 2 && !hasCorrelations) {
      suggestions.push({
        id: 'analyze-correlations',
        title: 'Analyze Correlations',
        description: 'Find relationships between your datasets',
        action: 'correlations',
        icon: TrendUp,
        priority: 'high',
        category: 'next-step',
      });
    }

    // Optimization suggestions
    if (hasCorrelations && currentTab !== 'visualize') {
      suggestions.push({
        id: 'create-visualizations',
        title: 'Create Visualizations',
        description: 'Turn your insights into compelling charts',
        action: 'visualize',
        icon: TrendUp,
        priority: 'medium',
        category: 'optimization',
      });
    }

    if (selectedDatasetCount > 0 && currentTab !== 'transform') {
      suggestions.push({
        id: 'transform-data',
        title: 'Transform Your Data',
        description: 'Clean and prepare data with transformation pipelines',
        action: 'transform',
        icon: TrendUp,
        priority: 'medium',
        category: 'optimization',
      });
    }

    // Learning suggestions
    suggestions.push({
      id: 'explore-tutorials',
      title: 'Learn Data Science Fundamentals',
      description: 'Follow guided tutorials to master data analysis',
      action: 'tutorials',
      icon: Users,
      priority: 'low',
      category: 'learning',
    });

    if (selectedDatasetCount > 2) {
      suggestions.push({
        id: 'export-analysis',
        title: 'Export Your Work',
        description: 'Save your analysis for external tools like R or Jupyter',
        action: 'export',
        icon: Clock,
        priority: 'medium',
        category: 'optimization',
      });
    }

    return suggestions.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };

      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const suggestions = generateSuggestions();
  const topSuggestions = suggestions.slice(0, 2);
  const moreSuggestions = suggestions.slice(2);

  const handleSuggestionClick = (suggestion: Suggestion) => {
    onSuggestionClick(suggestion.action);
    toast.success(`Navigating to ${suggestion.title.toLowerCase()}`);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Card className='border-dashed border-2 border-primary/20 bg-primary/5'>
      <CardHeader className='pb-3'>
        <CardTitle className='flex items-center gap-2 text-lg'>
          <Lightbulb className='w-5 h-5 text-primary' />
          Smart Suggestions
        </CardTitle>
        <CardDescription>
          Personalized recommendations based on your current progress
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-3'>
        {/* Top suggestions - always visible */}
        {topSuggestions.map(suggestion => (
          <div
            key={suggestion.id}
            className='flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer'
            onClick={() => handleSuggestionClick(suggestion)}
          >
            <div className='p-2 rounded-lg bg-primary/10 text-primary'>
              <suggestion.icon className='w-4 h-4' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center gap-2 mb-1'>
                <h4 className='font-medium text-sm'>{suggestion.title}</h4>
                <Badge
                  variant='outline'
                  className={`text-xs ${getPriorityColor(suggestion.priority)}`}
                >
                  {suggestion.priority}
                </Badge>
              </div>
              <p className='text-xs text-muted-foreground'>
                {suggestion.description}
              </p>
            </div>
          </div>
        ))}

        {/* More suggestions - collapsible */}
        {moreSuggestions.length > 0 && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button
                variant='ghost'
                size='sm'
                className='w-full gap-2 text-muted-foreground hover:text-foreground'
              >
                {isExpanded ? (
                  <>
                    <ArrowUp className='w-4 h-4' />
                    Show Less
                  </>
                ) : (
                  <>
                    <ArrowDown className='w-4 h-4' />
                    Show {moreSuggestions.length} More Suggestion
                    {moreSuggestions.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className='space-y-3 mt-3'>
              {moreSuggestions.map(suggestion => (
                <div
                  key={suggestion.id}
                  className='flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer'
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <div className='p-2 rounded-lg bg-primary/10 text-primary'>
                    <suggestion.icon className='w-4 h-4' />
                  </div>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2 mb-1'>
                      <h4 className='font-medium text-sm'>
                        {suggestion.title}
                      </h4>
                      <Badge
                        variant='outline'
                        className={`text-xs ${getPriorityColor(suggestion.priority)}`}
                      >
                        {suggestion.priority}
                      </Badge>
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {suggestion.description}
                    </p>
                  </div>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
